import { useMemo, useState } from 'react';
import {
  Controller,
  FormProvider,
  SubmitHandler,
  useForm,
} from 'react-hook-form';

import { Cog } from 'lucide-react';

import { Accordion } from 'components/Accordion';
import { Button } from 'components/Button';
import { Checkbox } from 'components/Checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'components/Select';
import { Switch } from 'components/Switch';
import { RHFTextInput } from 'components/input/RHFTextInput';
import { Flex, Grid } from 'components/layout';
import ButtonLoader from 'components/loading/ButtonLoader';
import { Typography } from 'components/typography';
import { useToast } from 'hooks/useToaster';
import { updateShipperCheckInSOPs } from 'lib/api/updateShipperCheckInSOPs';
import { NormalizedLoad } from 'types/Load';
import ButtonText from 'types/enums/ButtonText';
import { ButtonNamePosthog } from 'types/enums/CustomPosthogEvents';

import { LoadSectionAccordionItem } from '../LoadInformation/Components';
import {
  ShipperCheckInInputs,
  ShipperCheckInTextInputProps,
  TimeUnits,
} from './types';

type ShipperCheckInSectionProps = { normalizedLoad: NormalizedLoad };
const SOPTextInput = (props: ShipperCheckInTextInputProps) => (
  <RHFTextInput {...props} />
);

export default function ShipperCheckInSection({
  normalizedLoad: load,
}: ShipperCheckInSectionProps) {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [activeTabs, setActiveTabs] = useState<string[]>([
    'updateShipperCheckInSOPs',
  ]);
  const defaultValues = useMemo<ShipperCheckInInputs>(() => {
    const parsedValues = {
      freightTrackingId: load.freightTrackingID,
      frequency: '',
      timeUnit: '',
      defaultCarrierCheckInSettings: false,
      pickupAppt: false,
      loaded: false,
      dropoffAppt: false,
      unloaded: false,
    };
    return parsedValues as ShipperCheckInInputs;
  }, [load]);
  const formMethods = useForm<ShipperCheckInInputs>({ defaultValues });
  const { control, handleSubmit } = formMethods;
  const onSubmitUpdateSOPs: SubmitHandler<ShipperCheckInInputs> = async (
    data
  ) => {
    setLoading(true);

    const res = await updateShipperCheckInSOPs(
      load.ID!,
      data.freightTrackingId,
      data.frequency,
      data.timeUnit,
      data.defaultCarrierCheckInSettings,
      data.pickupAppt,
      data.loaded,
      data.dropoffAppt,
      data.unloaded
    );

    if (res.isOk()) {
      toast({
        description: 'Successfully updated Shipper Check-in SOPs',
        variant: 'success',
      });
    } else {
      toast({
        description: res.error.message,
        variant: 'destructive',
      });
    }

    setLoading(false);
  };

  return (
    <Accordion type='multiple' value={activeTabs} onValueChange={setActiveTabs}>
      <LoadSectionAccordionItem
        label='Shipper Check-ins'
        icon={<Cog className='h-6 w-6' strokeWidth={1} />}
        name='configureShipperCheckInSOPs'
        activeTabs={activeTabs}
      >
        <div>
          <FormProvider {...formMethods}>
            <div>
              <form
                onSubmit={handleSubmit(onSubmitUpdateSOPs)}
                className='grid gap-4 grid-cols-1 mt-4 mx-0 w-full'
              >
                <Grid gap='xl' className='mb-4 w-full mx-0'>
                  <div>
                    <Typography variant='h5' className='text-neutral-900'>
                      Time triggers
                    </Typography>
                    <Typography
                      variant='body-sm'
                      className='mt-1 text-neutral-600'
                    >
                      The time in between sending check-in emails.
                    </Typography>
                  </div>
                  <SOPTextInput
                    name='frequency'
                    label='Frequency'
                    inputType='number'
                    defaultValue='12'
                  />
                  <div>
                    <label className='mb-2 text-neutral-500'>
                      Unit of time
                    </label>
                    <Controller
                      name='timeUnit'
                      control={control}
                      render={({ field }) => (
                        <Select
                          onValueChange={field.onChange}
                          value={field.value}
                        >
                          <SelectTrigger className='w-full mt-1'>
                            <SelectValue placeholder={TimeUnits.Hours} />
                          </SelectTrigger>
                          <SelectContent>
                            {Object.keys(TimeUnits).map((unit) => (
                              <SelectItem key={unit} value={unit}>
                                {unit}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    />
                  </div>
                  <Controller
                    name='defaultCarrierCheckInSettings'
                    control={control}
                    render={({ field }) => (
                      <Flex align='center' gap='sm'>
                        <Switch
                          checked={field.value}
                          onCheckedChange={field.onChange}
                          className='focus-visible:ring-success-900 data-[state=checked]:bg-success-400 data-[state=unchecked]:bg-success-200'
                        />
                        <label
                          htmlFor='defaultCarrierCheckInSettings'
                          className='leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70'
                        >
                          Set check-in cadence as default
                        </label>
                      </Flex>
                    )}
                  />
                </Grid>
                <Grid gap='xl' className='mt-4 mb-4 w-full mx-0'>
                  <div>
                    <Typography variant='h5' className='text-neutral-900'>
                      Event Triggers
                    </Typography>
                    <Typography
                      variant='body-sm'
                      className='mt-1 text-neutral-600'
                    >
                      Select specific check-in points in the load process.
                    </Typography>
                  </div>
                  <Controller
                    name='pickupAppt'
                    control={control}
                    render={({ field }) => (
                      <Checkbox
                        label='At pickup appointment'
                        labelClassName='leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70'
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    )}
                  />
                  <Controller
                    name='loaded'
                    control={control}
                    render={({ field }) => (
                      <Checkbox
                        label='Loaded'
                        labelClassName='leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70'
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    )}
                  />
                  <Controller
                    name='dropoffAppt'
                    control={control}
                    render={({ field }) => (
                      <Checkbox
                        label='At dropoff appointment'
                        labelClassName='leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70'
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    )}
                  />
                  <Controller
                    name='unloaded'
                    control={control}
                    render={({ field }) => (
                      <Checkbox
                        label='Unloaded'
                        labelClassName='leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70'
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    )}
                  />
                </Grid>
                <Button
                  buttonNamePosthog={ButtonNamePosthog.ScheduleShipperEmails}
                  type='submit'
                  className='w-full mt-8'
                  disabled={loading}
                  logProperties={{
                    loadID: load.ID,
                    freightTrackingID: load.freightTrackingID,
                    serviceID: load.serviceID,
                  }}
                >
                  {loading ? (
                    <ButtonLoader />
                  ) : (
                    ButtonText.ScheduleShipperEmails
                  )}
                </Button>
              </form>
            </div>
          </FormProvider>
        </div>
      </LoadSectionAccordionItem>
    </Accordion>
  );
}
